import { FC, HTMLAttributes, useEffect, useState, useMemo, useRef, useCallback } from "react";
import styled, { css, keyframes } from "styled-components";
import WinnerImg from "assets/images/apng/winner.png";
import { getRandomRange } from "utils/number";
import { AnimButton, Center, Header as HeaderStyle } from "utils/constants";
import GreenSmokeImg from "assets/images/apng/green-smoke.png";
import FirstFlushImg from "assets/images/apng/first-flush.png";
import AnyFlushImg from "assets/images/apng/any-flush.png";
import StraightFlushImg from "assets/images/apng/straight-flush.png";
import RoyalFlushImg from "assets/images/apng/royal-flush.png";
import { useSummary, useJackpots, usePlayer, useSkip, useTournamentDetails, useCurrency } from "hooks";
import { ApngPlayer } from "components/ApngPlayer";
import { useStore } from "utils/store";
import { StageSize, SpinDirection, PokerHand, MinHeight, MediaQuery } from "types";
import { Spinner } from "../Spinner";
import { TextStyle } from "pixi.js";
import { animated, easings, SpringValue, useSpring, useSprings } from "@react-spring/web";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import { useDebounceCallback, useEventListener, useTimeout, useWindowSize } from 'usehooks-ts';
import { useOrientation } from "hooks/orientation";
import { useSkinnedAssets } from "hooks/skin";
import { useTranslation } from "react-i18next";

const active = keyframes`
  100% { color: #0f0; }
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Logo = styled.div<{ $logo: string }>`
  width: 100%;
  height: 10.5vh;
  background: url(${props => props.$logo}) center / contain no-repeat;
  position: relative;
  margin: 0 auto 1vh;
`;

const Border = styled.div<{ $height: number }>`
  position: relative;
  width: 100%;
  border-image-source: url(images/lhs-container.svg);
  border-image-slice: 32 fill;
  border-image-width: auto;
  border-radius: 10px;
  padding: 3.8vh 0 5.7vh;

  @media (${MediaQuery.Desktop}) {
    height: calc(100% - ${props => props.$height * 0.21 + 16}px);
    min-height: 465px;
  }

  @media (${MediaQuery.ACR}) {
    height: calc(100% - ${props => props.$height * 0.095 + 16}px);
  }
`;

const Image = styled(animated.img)`
  object-fit: contain;
`;

const Group = styled.div`
  width: calc(100% - 50px);
  padding: 0 25px 0;
  position: relative;
`;

const BountiesGroup = styled(Group)`
  width: calc(100% - 42px);
  padding: 0 21px 0;
`;

const BigBountiesGroup = styled(Group)`
  background-image: url(images/big-bounties.png);
  background-size: cover;
  aspect-ratio: 256 / 139;
  padding: 0;
  width: calc(100% + 42px);
  margin-left: -22px;
  margin-top: -20px;
  margin-bottom: -10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const BigBountiesInner = styled.div`
  width: 73%;
  margin-left: 1%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  height: 34%;
  row-gap: 16%;
  padding-top: 2%;
`;

const HeaderContainer = styled(Center)`
  width: 100%;
  height: clamp(1.375rem, 0.101rem + 4.337vh, 2.5rem);
  position: relative;
  margin-bottom: clamp(0.188rem, -0.166rem + 1.205vh, 0.5rem);

  & > ${Image} {
    margin-right: 3%;
  }
`;

const TextBox = styled(animated.div)<{ $active?: boolean }>`
  width: 31%;
  margin: clamp(0.125rem, -0.017rem + 0.482vh, 0.25rem) 0;
  background-image: linear-gradient(to bottom, #6e6157 3%, #61533b 21%, #cdc28e 54%, #5b4e36 78%, #6c5e51);
  border-radius: 5px;
  position: relative;
  color: ${props => props.$active ? '#0f0' : '#fff6cf'};
  font-size: clamp(1rem, 0.717rem + 0.964vh, 1.25rem);
  font-weight: normal;
  letter-spacing: normal;
  text-align: center;
  z-index: 0;
  user-select: none;

  &::before {
    content: "";
    position: absolute;
    left: 1px;
    top: 1px;
    width: calc(100% - 2px);
    height: calc(100% - 2px);
    border-radius: 5px;
    background-color: #000;
    z-index: -1;
  }
`;

const BigBountiesTextBox = styled(TextBox)`
  width: 30%;
  background: none;
  border: none;
  margin: 0;

  &::before {
    background: none;
  }
`;


const BountyTextBox = styled(TextBox)`
  border-radius: 8px;  
  margin: 0 auto;
  padding: 2px 0;
  width: calc(100% - 14px);
  height: clamp(1.625rem, 0.917rem + 2.41vh, 2.25rem);

  &::before {
    border-radius: 7px;  
    left: 2px;
    top: 2px;
    width: calc(100% - 4px);
    height: calc(100% - 4px);
  }

  &::after {
    content: "";
    position: absolute;
    left: -8px;
    top: calc(100% / 2 - 4px);
    width: calc(100% + 16px);
    height: 100%;
    background-image: conic-gradient(rgba(0, 0, 0, 0) 45deg, #968963 45deg, #c9be8a 135deg, rgba(0, 0, 0, 0) 135deg), conic-gradient(rgba(0, 0, 0, 0) 225deg, #c9be8a 225deg, #968963 315deg, rgba(0, 0, 0, 0) 315deg);
    background-size: 8px 8px;
    background-position: 0 0, calc(100% - 1px) 0;
    background-repeat: no-repeat;
  }
`;

const Text = styled(animated.p)`
  margin: clamp(0rem, -0.283rem + 0.964vh, 0.25rem) 0;  
  font-size: clamp(0.938rem, 0.584rem + 1.205vh, 1.25rem);
  font-weight: normal;
  letter-spacing: 0.58px;
  line-height: 0.9;
  text-align: center;
  color: #d29b75;
`;

const Label = styled(Text)<{ $active?: boolean }>`
  margin-top: 0.6vh;
  letter-spacing: 0.7px;
  ${props => {
    if(props.$active) return css`color: #0f0`;
  }}
`;

const Row = styled(Center)`
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-between;
  position: relative;
  margin: 0 auto;
`;

const Table = styled.table<{ $jackpotWon: boolean, $skip: boolean }>`
  margin: clamp(0rem, -1.133rem + 3.855vh, 1rem) auto;
  width: calc(100% - 40px);
  border-collapse: collapse;
  font-size: clamp(0.938rem, 0.584rem + 1.205vh, 1.25rem);
  font-weight: normal;
  line-height: 1;
  letter-spacing: normal;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);

  & tr {
    height: clamp(1.063rem, 0.425rem + 2.169vh, 1.625rem);
  }

  & th:first-child,
  td:first-child {
    text-align: left;
  }

  & th:nth-child(2), td:nth-child(2) {
    width: 40%;
  }

  & th,
  td {
    &.checked {
      ${props => {
        if(props.$skip) return css`color: #0f0;`;
      }}
      animation: ${active} 0s linear ${props => !props.$jackpotWon ? 1.5 : 3.5}s forwards;
    }

    & ${TextBox} {
      margin: 0 auto;
      width: clamp(0.938rem, 0.584rem + 1.205vh, 1.25rem);
      height: clamp(0.938rem, 0.584rem + 1.205vh, 1.25rem);
      border-radius: 3px;

      &::before {
        border-radius: 3px;
      }

      & > ${Image} {
        width: 220%;
        left: -55%;
        top: -55%;
        position: absolute;
      }
    }
  }
`;

const PayoutLabel = styled(animated.div)<{ $active?: boolean, $skip: boolean }>`
  width: 31%;
  font-size: clamp(1rem, 0.717rem + 0.964vh, 1.25rem);
  font-weight: normal;
  line-height: 1.13;
  letter-spacing: normal;
  text-align: center;
  color: ${props => props.$skip ? '#0f0' : 'rgba(255, 255, 255, 0.4)'};
  ${props => {
    if(props.$active) return css`animation: ${active} 0.2s linear 1.25s forwards;`;
  }}
`;

const Skip = styled(AnimButton)<{ $active: boolean }>`
  text-decoration: underline;
  position: relative;
  width: 70%;
  margin-top: clamp(0rem, -1.699rem + 5.783vh, 1.5rem);
  margin-left: 15%;
  margin-right: 15%;
  letter-spacing: 0.47px;
  line-height: 0.75;
  font-family: Teko;
  font-size: clamp(0.875rem, 0.45rem + 1.446vh, 1.25rem);
  font-weight: normal;
  color: #b79988;
  background: none;
  border: none;
  white-space: nowrap;
  pointer-events: ${props => props.$active ? 'auto' : 'none'};

  & > ${Image} {
    margin-left: 5.5%;
  }
`;

const Button = styled(AnimButton)<{ $disable: boolean }>`
  position: absolute;
  padding: 6px 0 4px;
  width: 64%;
  left: calc((100% - 64%) / 2);
  bottom: -6px;
  border-radius: 5px;
  font-family: Teko;
  font-size: clamp(1rem, 0.717rem + 0.964vh, 1.25rem);
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  letter-spacing: normal;
  text-align: center;
  border: solid 1px #ffe893;
  ${props => {
    if(props.$disable) 
      return css`
        box-shadow: 0 0 0 0 #4d2e05, inset 0 3px 0 0 #82764b;
        background-image: linear-gradient(to bottom, #6b5233 0%, #827e6b 52%, #6b5132 100%);
        color: #3c3120;
        text-shadow: 0 2px 0 rgba(255, 255, 255, 0.2);
        border-color: #82764b;
      `;
    else
    return css`
      box-shadow: 0 0 10px 0 #ff8900, inset 0 2px 0 0 #fff;
      background-image: linear-gradient(to bottom, #cf9b5e 0%, #efe090 50%, #cf9b5e 100%);
      color: #000;
      text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
    `;
  }}
`;

const Winner = styled(animated.div)<{ $top: number }>`
  width: clamp(7.625rem, 1.821rem + 19.759vh, 12.75rem);
  height: clamp(2.375rem, 0.535rem + 6.265vh, 4rem);
  top: calc(${props => props.$top}px - clamp(10rem, 0.204rem + 24.49vh, 13.75rem));
  left: calc((100% - clamp(7.625rem, 1.821rem + 19.759vh, 12.75rem)) / 2);
  position: absolute;
  pointer-events: none;

  @media (${MediaQuery.ACR}) {
    top: calc(${props => props.$top}px - clamp(4rem, 0.319rem + 12.53vh, 7.25rem));
  }
`;

const AnimContainer = styled(Center)`
  position: fixed;
  z-index: 1;
  pointer-events: none;

  & > ${Image} {
    position: absolute;
  }
`;

const Header = styled(HeaderStyle)`
  margin-top: 1%;
  font-size: clamp(1.375rem, 0.229rem + 3.902vh, 2.375rem);
`;

const HeaderBlack = styled.div`
  text-shadow: 0 1px 0 #fff, 0 1px 0 #fff;
  font-family: Teko;
  font-size: clamp(1.625rem, 1.195rem + 1.463vh, 2rem);
  font-weight: 500;
  line-height: 0.51;
  color: #000;
  margin-top: 3%;
`;  

export type BountiesProps = HTMLAttributes<HTMLDivElement> & {
  onSkip: () => void;
  onScratcher: () => void;
  onFlushUpdate: () => void;
  onBankUpdate: () => void;
  onJackpot: () => void;
  bountyPlayer: string;
  isEliminated: boolean;
  isBountyDone: boolean;
};

export const Bounties: FC<BountiesProps> = ({ onSkip, onScratcher, onFlushUpdate, onBankUpdate, onJackpot, bountyPlayer, isEliminated, isBountyDone, ...props }) => {
  const { bountyLeaderboard, winType } = useTournamentDetails();
  const [payoutShown, setPayoutShown] = useState(-1);
  const jackpots = useJackpots();
  const { firstFlush, secondFlush, firstFlushElapsed, secondFlushElapsed } = useSummary();
  const { winnings, bank } = usePlayer();
  const showBank = useStore(state => state.showBank);
  const [curBank, setCurBank] = useState(-1);
  const textBoxRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState<StageSize>({
    width: 0,
    height: 0,
  });
  const skip = useSkip();
  const [featuredPlayers, setFeaturedPlayers] = useState<string[]>([]);
  const tableRef = useRef<HTMLTableElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef(0);
  const { parseCurrency } = useCurrency();
  const textStyle = useRef(new TextStyle({
    align: "center",
    fontFamily: "Teko",
    fontWeight: "500",
    fill: "#0f0",
  }));
  const winnerRef = useRef<HTMLDivElement>(null);
  const springOpacity = useMemo(() => {
    return new SpringValue(1);
  }, []);
  const play = useSoundStore(store => store.play);
  const getHowl = useSoundStore(store => store.getHowl);
  const { width = 0, height = 0 } = useWindowSize();
  const { landscape } = useOrientation();
  const { logoSidebar } = useSkinnedAssets();
  const { t } = useTranslation();
  const label = [t('Flush'), t('Straight Flush'), t('Royal')];

  const handleResize = () => {
    textStyle.current = new TextStyle({
      ...textStyle.current,
      fontSize: Math.min(36, Math.max(28, 18.9 + height * 0.02)),
    });
    sizeRef.current = Math.min(13, Math.max(10, 6.602 + 0.116 * height / 16));
    setStageSize({
      width: (textBoxRef.current?.clientWidth || 0) - 4,
      height: (textBoxRef.current?.clientHeight || 0) - 4,
    });
  };

  const debounceResize = useDebounceCallback(handleResize, 250);
  useEventListener('resize', debounceResize);

  useEffect(() => {
    handleResize();
  }, []);

  const skipAnimation = useMemo(() => {
    return firstFlushElapsed || (skip && !!firstFlush);
  }, [firstFlush, firstFlushElapsed, skip]);

  useTimeout(() => {
    setPayoutShown(skipAnimation ? 4 : 0);
  }, skipAnimation ? 0 : !!firstFlush ? 1000 : null);

  const isSecondFlush = useMemo(() => {
    return (!!secondFlush && isBountyDone) || secondFlushElapsed;
  }, [isBountyDone, secondFlush, secondFlushElapsed]);

  useEffect(() => {
    if(!secondFlushElapsed || skip || !isSecondFlush) return;
    setPayoutShown(5);
  }, [isSecondFlush, secondFlushElapsed, skip]);

  useTimeout(onFlushUpdate, (!winnings && !winType) || (!!firstFlush && payoutShown < 4) || skip ? null : 1250);

  useEffect(() => {
    if(curBank === -1) setCurBank(bank);
  }, [bank, curBank]);

  useEffect(() => {
    if(showBank && curBank === bank) onBankUpdate();
  }, [bank, curBank, onBankUpdate, showBank]);

  useEffect(() => {
    if(!featuredPlayers.includes(bountyPlayer)){
      setFeaturedPlayers(featuredPlayers.concat(bountyPlayer));
    }
  }, [featuredPlayers, bountyPlayer]);

  const bounties = useMemo(() => {
    if(!bank) return [0];
    return getRandomRange(Math.max(0, curBank), bank);
  }, [bank, curBank]);

  const getFlushIndex = (s: PokerHand | string) => {
    switch(s){
      case PokerHand.Flush:
        return 0;
      case PokerHand.StraightFlush:
        return 1;
      default:
        return 2;
    }
  };

  const flushStyle = useMemo(() => {
    if(!firstFlush) return;

    const table = tableRef.current?.getBoundingClientRect();
    const child = labelRef.current?.children[!isSecondFlush ? payoutShown - 1 : 1]?.getBoundingClientRect();

    const size = {
      width: `${sizeRef.current}rem`,
      height: `${sizeRef.current}rem`,
    }
    
    switch(payoutShown){
      case 0:
        return {
          ...size,
          top: `calc(${table ? table.y : 0}px - ${sizeRef.current}rem * 0.5 + ${3.5 + getFlushIndex(firstFlush) * 3}vh)`,
          left: `calc(${table ? table.x : 0}px - ${sizeRef.current}rem * 0.3)`,
        };
      case 4:
        return {
          ...size,
          top: `calc(${child ? child.y : 0}px - ${sizeRef.current}rem * 0.55)`,
          left: `calc(${child ? child.x + child.width * 0.5 : 0}px - ${sizeRef.current}rem * 0.5)`,
        };
      default:
        return {
          ...size,
          top: `calc(${child ? child.y : 0}px - ${sizeRef.current}rem * 0.39)`,
          left: `calc(${child ? child.x + child.width * 0.5 : 0}px - ${sizeRef.current}rem * 0.5)`,
        };
    }
  }, [firstFlush, isSecondFlush, payoutShown]);

  const showCheck = (flush: string | undefined, flushType: PokerHand) => {
    return payoutShown > -1 && flush === flushType;
  };

  const debBankUpdate = useDebounceCallback(() => {
    if(showBank) onBankUpdate();
  }, skip ? 0 : 500);

  const handleSpin = () => {
    setCurBank(bank);
    debBankUpdate();
  };

  const [springs, api] = useSprings(
    jackpots.length,
    () => ({
      transform: 'scale(1) translateX(0%)',
      width: '31%',
      opacity: 1,
      display: 'block',
      config: key => {
        return { 
          duration: key === 'opacity' ? 250 : 500,
          easing: easings.easeInSine,
        }
      }
    })
  );

  const [winSpring, winApi] = useSpring(() => ({
    filter: 'blur(4px)',
    transform: 'translate(0, 0)',
    opacity: 0,
    config: {
      duration: 250,
      easing: easings.easeInCirc
    }
  }));

  useEffect(() => {
    if(secondFlushElapsed){
      springOpacity.set(0);
      winApi.set({
        opacity: 1,
        filter: 'blur(0)',
        transform: 'translate(0, 0)',
      });
    }
  }, [secondFlushElapsed, springOpacity, winApi]);

  const showWinner = useCallback(() => {
    const rect = winnerRef.current?.getBoundingClientRect();
    const parent = winnerRef.current?.offsetParent;
    if(rect && parent){
      onJackpot();
      springOpacity.set(0);
      if(!getHowl(Sounds.Woosh).playing()) play(Sounds.Woosh);
      const x = (rect.width - parent.clientWidth) / 2 + parent.clientWidth + width * 0.02;
      const y = -Math.max(220, Math.min(458, height * 0.57 - 49.5));
      winApi.set({
        opacity: 1,
        transform: `translate(${x}px, ${y}px)`,
      });
      winApi.start({
        filter: 'blur(0)',
        transform: 'translate(0, 0)',
      });
    }
  }, [getHowl, height, onJackpot, play, springOpacity, width, winApi]);

  const showJackpot = useCallback((i: number) => {
    if(!isSecondFlush) return false;

    switch(secondFlush){
      case PokerHand.Flush:
        return i === 0;
      case PokerHand.StraightFlush:
        return i === 1;
      case PokerHand.RoyalFlush:
        return i === 2;
    }
  }, [isSecondFlush, secondFlush]);

  const debShowWinner = useDebounceCallback(showWinner, 500);

  useEffect(() => {
    const properties = {
      transform: 'scale(1) translateX(0%)',
      width: '100%',
      opacity: 1,
    };

    if(payoutShown >= 0 && payoutShown < 4){
      play(Sounds.Flush);
    }
    
    if(payoutShown === 4) {
      if(!firstFlushElapsed) onFlushUpdate();
    }else if(payoutShown === 5 && isSecondFlush) {
      if(!secondFlushElapsed){
        api.start(() => ({
          ...properties,
          onRest: debShowWinner
        }));
      }else{
        api.set(i => {
          if(showJackpot(i)){
            return properties;
          }else{
            return {
              display: 'none',
            };
          }
        });
      }
    }
  }, [api, debShowWinner, firstFlushElapsed, isSecondFlush, onFlushUpdate, payoutShown, play, secondFlushElapsed, showJackpot]);

  const { opacity } = useSpring({
    from: { opacity: 0 },
    to: { opacity: isSecondFlush ? 1 : 0 },
    delay: secondFlushElapsed ? 0 : 3500,
    config: {
      duration: 0,
    }
  });

  useEffect(() => {
    if(!isSecondFlush || secondFlushElapsed || skip) return;
    api.start(i => {
      if(showJackpot(i)){
        return {
          transform: 'scale(4) translateX(115%)',
          opacity: 0,
          onRest: () => {
            play(Sounds.Flush);
          }
        };
      }else{
        return {
          display: 'none',
        };
      }
    });

    return () => {
      api.stop();
    };
  }, [api, isSecondFlush, play, secondFlushElapsed, showJackpot, skip]);

  const handleEnd = useCallback(() => {
    setPayoutShown(payoutShown + 1);
  }, [payoutShown]);

  const delay = useMemo(() => {
    return !isSecondFlush ? 0 : 0.5;
  }, [isSecondFlush]);

  const rate = useMemo(() => {
    return isSecondFlush ? 1.5 : 2;
  }, [isSecondFlush]);

  const playJackpot = (n: number) => {
    return showJackpot(n) && !secondFlushElapsed;
  };

  return (
    <Container>
      {landscape && height >= MinHeight && <Logo $logo={logoSidebar} />}
      <Border $height={height} data-testid="bounties">
        <BountiesGroup>
          <HeaderContainer>
            <Image src="images/bounty-green.webp" alt="bounty green" height="100%" />
            <Header>{t('BOUNTIES WON')}</Header>
          </HeaderContainer>
          <BountyTextBox ref={textBoxRef}>
            <Spinner
              {...stageSize}
              textStyle={textStyle.current}
              options={bounties.map(bounty => parseCurrency(bounty))}
              spinTo={parseCurrency(bank)}
              spinSeconds={2}
              spinDirection={SpinDirection.Up}
              onSpinFinish={handleSpin}
              skip={skip}
            />
          </BountyTextBox>
        </BountiesGroup>
        <BigBountiesGroup>
          <HeaderBlack>{t('BIG BOUNTIES TRACKER')}</HeaderBlack>
          <BigBountiesInner>
            {bountyLeaderboard.map((value, index) => <BigBountiesTextBox key={index} $active={featuredPlayers.includes(value.playerId)} data-testid={`bounties-top-${index}`}>{parseCurrency(value.bounty)}</BigBountiesTextBox>)}
          </BigBountiesInner>
        </BigBountiesGroup>
        <Group>
          <HeaderContainer>
            <Image src="images/bounty-gold.webp" alt="bounty gold" height="100%" />
            <Header>{t('Jackpots').toUpperCase()}</Header>
          </HeaderContainer>
          <Text>{t('Win two hands with flushes')}</Text>
          <Text>{t('and collect a jackpot!')}</Text>
          <Table ref={tableRef} $jackpotWon={isSecondFlush} $skip={(firstFlushElapsed && !isSecondFlush) || secondFlushElapsed}>
            <tbody>
              <tr>
                <td></td>
                <td>{t('1st')}</td>
                <td>{t('2nd')}</td>
              </tr>
              <tr>
                <td className={[firstFlush, isSecondFlush ? secondFlush : ''].includes(PokerHand.Flush) && payoutShown >= 0 ? "checked" : ""}>{t('Flush')}</td>
                <td><TextBox>{showCheck(firstFlush, PokerHand.Flush) && <Image src="images/check.png"alt="check" />}</TextBox></td>
                <td><TextBox>{showCheck(isSecondFlush ? secondFlush : '', PokerHand.Flush) && <Image src="images/check.png"alt="check" style={{ opacity }} />}</TextBox></td>
              </tr>
              <tr>
                <td className={[firstFlush, isSecondFlush ? secondFlush : ''].includes(PokerHand.StraightFlush) && payoutShown >= 0 ? "checked" : ""}>{t('Straight Flush')}</td>
                <td><TextBox>{showCheck(firstFlush, PokerHand.StraightFlush) && <Image src="images/check.png"alt="check" />}</TextBox></td>
                <td><TextBox>{showCheck(isSecondFlush ? secondFlush : '', PokerHand.StraightFlush) && <Image src="images/check.png"alt="check" style={{ opacity }} />}</TextBox></td>
              </tr>
              <tr>
                <td className={[firstFlush, isSecondFlush ? secondFlush : ''].includes(PokerHand.RoyalFlush) && payoutShown >= 0 ? "checked" : ""}>{t('Royal Flush')}</td>
                <td><TextBox>{showCheck(firstFlush, PokerHand.RoyalFlush) && <Image src="images/check.png"alt="check" />}</TextBox></td>
                <td><TextBox>{showCheck(isSecondFlush ? secondFlush : '', PokerHand.RoyalFlush) && <Image src="images/check.png"alt="check" style={{ opacity }} />}</TextBox></td>
              </tr>
            </tbody>
          </Table>
          <Label $active={payoutShown > 0} style={{ opacity: springOpacity }}>{t('2nd Flush Payouts')}</Label>
          <Row style={{ zIndex: isSecondFlush ? 1 : 0 }}>
            {springs.map((props, index) => <TextBox key={index} style={{ transform: props.transform, width: props.width, display: props.display, color: !!jackpots[index] && payoutShown > index ? '#0f0' : 'rgba(0, 0, 0, 0)' }}>{parseCurrency(jackpots[index], true)}</TextBox>)}
          </Row>
          <Row ref={labelRef} style={{ justifyContent: 'space-around' }}>
            {springs.map((props, index) => <PayoutLabel key={index} $active={payoutShown > index} $skip={firstFlushElapsed} style={{ opacity: props.opacity, display: props.display }}>{label[index]}</PayoutLabel>)}
          </Row>
          <Skip $active={!skip} onClick={onSkip} data-testid="bounties-skip-button">{t('SKIP THIS HAND')}<Image src="images/arrow-forward.png" alt="skip" width="9%" /></Skip>
          {!skip && <AnimContainer style={flushStyle}>
            {payoutShown === 0 &&
              <>
                <ApngPlayer
                  src={GreenSmokeImg}
                  style={{ 
                    position: "absolute"
                  }}
                  rate={rate}
                  delay={delay}
                  onEnd={handleEnd}
                />
                <ApngPlayer
                  src={FirstFlushImg}
                  style={{ 
                    position: "absolute",
                    height: "75%"
                  }}
                  rate={rate}
                  delay={delay}
                />
              </>
            }
            {(payoutShown === 1 || playJackpot(0)) &&
              <>
                <ApngPlayer
                  src={GreenSmokeImg}
                  style={{ 
                    position: "absolute"
                  }}
                  rate={rate}
                  delay={delay}
                  onEnd={handleEnd}
                />
                <ApngPlayer
                  src={AnyFlushImg}
                  style={{ 
                    position: "absolute",
                    height: "75%"
                  }}
                  rate={rate}
                  delay={delay}
                />
              </>
            }
            {(payoutShown === 2 || playJackpot(1)) &&
              <>
                <ApngPlayer
                  src={GreenSmokeImg}
                  style={{ 
                    position: "absolute"
                  }}
                  rate={rate}
                  delay={delay}
                  onEnd={handleEnd}
                />
                <ApngPlayer
                  src={StraightFlushImg}
                  style={{ 
                    position: "absolute",
                    height: "75%"
                  }}
                  rate={rate}
                  delay={delay}
                />
              </>
            }
            {(payoutShown === 3 || playJackpot(2)) &&
              <>
                <ApngPlayer
                  src={GreenSmokeImg}
                  style={{ 
                    position: "absolute"
                  }}
                  rate={rate}
                  delay={delay}
                  onEnd={handleEnd}
                />
                <ApngPlayer
                  src={RoyalFlushImg}
                  style={{ 
                    position: "absolute",
                    height: "75%"
                  }}
                  rate={rate}
                  delay={delay}
                />
              </>
            }
          </AnimContainer>}
        </Group>
        <Winner ref={winnerRef} style={winSpring} $top={labelRef.current?.getBoundingClientRect().y || 0}>
          <ApngPlayer
            src={WinnerImg}
            rate={1.5}
            delay={1}
            numPlays={0}
            yoyo={true}
          />
        </Winner>
        <Button $disable={isEliminated} onClick={onScratcher}>{t('OPEN RESULTS SCRATCHER')}</Button>
      </Border>
    </Container>
  );
};
